import { Field } from "@atlaskit/form";
import TextArea from "@atlaskit/textarea";
import React, { ChangeEvent, Suspense, useState } from "react";
import { FlagGroup } from "@atlaskit/flag";
import { FormattedMessage } from "react-intl";
import { getLocale, bitbucketCommit } from "../../config";
import { FEEDBACK_CONTEXT_FIELD_ID, ENTRYPOINT_ID, SOURCE } from "./constant";
import { UserAccount } from "../../clients/gateway-client/types";
import { Site } from "../../hooks/useSite";
import { isHello } from "../../utils/isHello";
import { useManifestVersion } from "../../hooks/useManifestVersion";
import { ANALYTICS_UI_SUBJECTS, ANALYTICS_SCREEN_NAMES } from "../../analytics/analyticsEvents";
import { useSendAnalyticEvent } from "../../analytics/analyticsClient";
import { messages } from "./messages";
import { bifrostEnv } from "../../config";

const AkFeedbackCollector = React.lazy(() =>
  import("@atlaskit/feedback-collector").then((module) => {
    return {
      default: module.default,
    };
  }),
);

const AkFeedbackFlag = React.lazy(() =>
  import(/* webpackChunkName: "feedback-flag" */ "@atlaskit/feedback-collector").then(({ FeedbackFlag }) => {
    return {
      default: FeedbackFlag,
    };
  }),
);

export const createSummary = (feedback: string): string => {
  const summary = `[CHROME - ${bifrostEnv}] ` + feedback;
  if (summary.length >= 255) {
    return summary.substring(0, 250) + "...";
  }

  return `Environment: ${bifrostEnv}\n${summary}`;
};

export const getFeedbackContext = (browserManifestVersion: string, site: Site, userAccount: UserAccount): string => {
  const feedbackContext = {
    version: bitbucketCommit,
    release: browserManifestVersion,
    source: SOURCE,
    userId: userAccount.accountId,
    cloudId: site.workspace.cloudId,
    isHello: isHello(site.workspace.cloudId),
    environment: bifrostEnv,
  };

  return JSON.stringify(feedbackContext);
};

export interface FeedbackModalProps {
  site: Site;
  userAccount: UserAccount;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

const FeedbackModal = ({ site, userAccount, showModal, setShowModal }: FeedbackModalProps): React.JSX.Element => {
  const { browserManifestVersion } = useManifestVersion();
  const [description, setDescription] = useState<string>("");
  const [displayFlag, setDisplayFlag] = useState(false);

  const sendAnalyticEvent = useSendAnalyticEvent();

  const hideFlag = (): void => setDisplayFlag(false);

  const onSubmit = (): void => {
    setDisplayFlag(true);

    // https://data-portal.internal.atlassian.com/analytics/registry/73850
    sendAnalyticEvent({
      eventType: "ui",
      event: {
        action: "submitFeedback",
        actionSubject: ANALYTICS_UI_SUBJECTS.FEEDBACK_COLLECTOR,
        source: ANALYTICS_SCREEN_NAMES.EXTENSION_HOME_SCREEN,
      },
      userId: userAccount.accountId,
    });
  };

  const close = (): void => {
    setShowModal(false);
    setDescription("");
  };

  return (
    <>
      {showModal && (
        <Suspense fallback={<></>}>
          <AkFeedbackCollector
            additionalFields={[
              {
                id: FEEDBACK_CONTEXT_FIELD_ID,
                value: getFeedbackContext(browserManifestVersion, site, userAccount),
              },
            ]}
            atlassianAccountId={userAccount.accountId}
            customContent={
              <>
                <Field
                  defaultValue=""
                  isRequired
                  label={<FormattedMessage {...messages.feedbackCollectorLabel} />}
                  name="description-field"
                >
                  {({ fieldProps }: any) => (
                    <TextArea
                      {...fieldProps}
                      minimumRows={6}
                      name="feedback-collector-description"
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                      placeholder=""
                      value={description}
                    />
                  )}
                </Field>
              </>
            }
            descriptionDefaultValue={description}
            email={userAccount.email}
            entrypointId={ENTRYPOINT_ID}
            locale={getLocale()}
            name={userAccount.name}
            onClose={close}
            onSubmit={onSubmit}
            showDefaultTextFields={false}
            showTypeField={false}
            summaryDefaultValue={createSummary(description)}
          />
        </Suspense>
      )}
      <FlagGroup onDismissed={hideFlag}>
        {displayFlag && (
          <Suspense fallback={<></>}>
            <AkFeedbackFlag locale={getLocale()} />
          </Suspense>
        )}
      </FlagGroup>
    </>
  );
};

export default FeedbackModal;
